import { useEffect, useState } from "react";
import { api } from "../../api/apiClient";
import type {
  AiEditableSystemKey,
  AiEditingState,
  AiPhotoSearchTrial,
  AiPhotoSearchTrialSide,
  AiTestablePastSearch,
} from "../../api/types";
import { useT } from "../../i18n/LanguageProvider";
import { money } from "../../i18n/intl";
import { colourLabel, flowerTypeLabel } from "../../i18n/taxonomy";
import { PhotoSearchCandidateCard } from "../shop-floor/PhotoSearchCandidateCard";
import { fmtAiWhen, pickPlace } from "./aiControlUi";

/**
 * Searches tested at once. One: the cap is checked before each search, and
 * running two would let both pass it before either is on the ledger.
 */
const AT_ONCE = 1;

type Outcome = { trial: AiPhotoSearchTrial } | { error: string } | { running: true };

interface Props {
  systemKey: AiEditableSystemKey;
  editing: AiEditingState;
  onBack: () => void;
  /** After a test (the draft is now tested) or a publish: the page reloads. */
  onChanged: () => void;
}

/**
 * G-149 S2 (OD-230) — the saved draft tried on past kiosk searches, beside
 * the live text: the same photo, today's stock at that shop, and the answer
 * drawn as the kiosk draws it (G-146). Every search tested is two real,
 * billed calls.
 */
export function AiDraftTest({ systemKey, editing, onBack, onChanged }: Props) {
  const [searches, setSearches] = useState<AiTestablePastSearch[] | null>(null);
  const [outcomes, setOutcomes] = useState<Record<string, Outcome>>({});
  const [selected, setSelected] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const photos = usePastSearchPhotos(systemKey, searches);

  useEffect(() => {
    let cancelled = false;
    api
      .aiTestablePastSearches(systemKey)
      .then((list) => {
        if (cancelled) return;
        setSearches(list);
        setSelected(list[0]?.id ?? null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Could not load past searches.");
      });
    return () => {
      cancelled = true;
    };
  }, [systemKey]);

  const trials = Object.values(outcomes).flatMap((o) => ("trial" in o ? [o.trial] : []));
  const liveFirst = trials.filter((t) => t.live.pickRank === 1).length;
  const draftFirst = trials.filter((t) => t.draft.pickRank === 1).length;
  const spent = trials.reduce((sum, t) => sum + Number(t.live.costEur ?? 0) + Number(t.draft.costEur ?? 0), 0);
  const untested = (searches ?? []).filter((s) => !outcomes[s.id] || "error" in outcomes[s.id]);

  async function testOne(id: string): Promise<boolean> {
    setOutcomes((prev) => ({ ...prev, [id]: { running: true } }));
    try {
      const trial = await api.testAiInstructions(systemKey, id);
      setOutcomes((prev) => ({ ...prev, [id]: { trial } }));
      return true;
    } catch (err) {
      setOutcomes((prev) => ({ ...prev, [id]: { error: err instanceof Error ? err.message : "The test failed." } }));
      return false;
    }
  }

  async function testAll() {
    const queue = untested.map((s) => s.id);
    if (queue.length === 0) return;
    setRunning(true);
    setError(null);
    let anyPassed = false;
    const worker = async () => {
      for (let id = queue.shift(); id !== undefined; id = queue.shift()) {
        if (await testOne(id)) anyPassed = true;
      }
    };
    await Promise.all(Array.from({ length: Math.min(AT_ONCE, queue.length) }, worker));
    setRunning(false);
    // The server marks the draft tested on the first success; show it.
    if (anyPassed) onChanged();
  }

  async function publish() {
    if (!editing.draft?.note) return;
    setPublishing(true);
    setError(null);
    try {
      await api.publishAiInstructions(systemKey, editing.draft.note);
      onChanged();
      onBack();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not publish.");
    } finally {
      setPublishing(false);
    }
  }

  const current = selected ? outcomes[selected] : undefined;
  const currentSearch = searches?.find((s) => s.id === selected) ?? null;
  // The server is the gate (OD-230): the draft as saved must have passed a test.
  const canPublish = Boolean(editing.draft?.tested && editing.draft.note) && !running;

  return (
    <section className="ai-test" aria-label="Test the change on past searches">
      <div className="ai-test-head">
        <button type="button" className="button plain" onClick={onBack} disabled={running || publishing}>
          Back to editing
        </button>
        <h2>Test the change on past searches</h2>
        <div className="ai-control-actions">
          <button
            type="button"
            className="button tinted"
            onClick={() => void testAll()}
            disabled={running || untested.length === 0}
          >
            {running ? "Testing…" : `Test on ${untested.length} ${untested.length === 1 ? "search" : "searches"}`}
          </button>
          <button type="button" className="button" onClick={() => void publish()} disabled={!canPublish || publishing}>
            {publishing ? "Publishing…" : editing.draft?.note ? "Publish the change" : "Name the change to publish"}
          </button>
        </div>
      </div>
      {error ? <div className="alert error">{error}</div> : null}

      <div className="ai-test-scores">
        <Score label="Past searches" value={String(searches?.length ?? "…")} />
        <Score label="Now live" value={`${liveFirst} / ${trials.length}`} sub="florist’s pick first" />
        <Score label="With your change" value={`${draftFirst} / ${trials.length}`} sub="florist’s pick first" good={draftFirst > liveFirst} />
        <Score label="Cost of this test" value={money(spent)} sub={`${trials.length * 2} calls`} />
      </div>

      {searches && searches.length === 0 ? <p className="muted">No past search to test on.</p> : null}
      {searches && searches.length > 0 ? (
        <div className="ai-test-searches" role="list" aria-label="Past kiosk searches">
          {searches.map((search) => (
            <div role="listitem" key={search.id}>
              <button
                type="button"
                className={`ai-test-thumb${search.id === selected ? " is-selected" : ""}`}
                aria-pressed={search.id === selected}
                onClick={() => setSelected(search.id)}
              >
                <PastSearchPhoto url={photos[search.id]} />
                <span className="ai-test-thumb-when">{fmtAiWhen(search.createdAt)}</span>
                <OutcomeMark outcome={outcomes[search.id]} />
              </button>
            </div>
          ))}
        </div>
      ) : null}

      {currentSearch ? (
        <div className="ai-test-selected">
          <div className="ai-test-selected-head">
            <span>
              <strong>{currentSearch.chosenItem.name}</strong>
              <span className="muted"> · {currentSearch.locationName} · {fmtAiWhen(currentSearch.createdAt)}</span>
            </span>
            {!current || "error" in current ? (
              <button
                type="button"
                className="button plain"
                onClick={() => void testOne(currentSearch.id).then((ok) => ok && onChanged())}
                disabled={running}
              >
                Test this one
              </button>
            ) : null}
          </div>
          {current && "error" in current ? <div className="alert error">{current.error}</div> : null}
          {current && "running" in current ? <p className="muted">Testing…</p> : null}
          {current && "trial" in current ? (
            <div className="ai-test-compare">
              <TrialColumn
                title={`Now live · ${current.trial.live.version}`}
                side={current.trial.live}
                chosenItemId={current.trial.chosenItemId}
                photoUrl={photos[currentSearch.id]}
              />
              <TrialColumn
                title="With your change"
                side={current.trial.draft}
                chosenItemId={current.trial.chosenItemId}
                photoUrl={photos[currentSearch.id]}
                better={rankBetter(current.trial.draft.pickRank, current.trial.live.pickRank)}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

/** Lower is better; "not offered" is worst. */
function rankBetter(a: number | null, b: number | null): boolean {
  if (a === null) return false;
  return b === null || a < b;
}

function Score({ label, value, sub, good }: { label: string; value: string; sub?: string; good?: boolean }) {
  return (
    <div className={`ai-test-score${good ? " is-good" : ""}`}>
      <span className="ai-test-score-label">{label}</span>
      <span className="ai-test-score-value">{value}</span>
      {sub ? <span className="muted">{sub}</span> : null}
    </div>
  );
}

function OutcomeMark({ outcome }: { outcome: Outcome | undefined }) {
  if (!outcome) return null;
  if ("running" in outcome) return <span className="ai-control-pill is-muted">Testing</span>;
  if ("error" in outcome) return <span className="ai-control-pill is-warning">Failed</span>;
  const { live, draft } = outcome.trial;
  return (
    <span className={`ai-control-pill ${rankBetter(draft.pickRank, live.pickRank) ? "is-live" : "is-muted"}`}>
      {pickPlace(live.pickRank)} → {pickPlace(draft.pickRank)}
    </span>
  );
}

/**
 * Each past search's photo, fetched once and shared by the strip and both
 * answers; the object URLs are let go when the view closes.
 */
function usePastSearchPhotos(
  systemKey: AiEditableSystemKey,
  searches: AiTestablePastSearch[] | null,
): Record<string, string | undefined> {
  const [urls, setUrls] = useState<Record<string, string | undefined>>({});
  useEffect(() => {
    if (!searches) return;
    let cancelled = false;
    const made: string[] = [];
    for (const search of searches) {
      void api.fetchAiPastSearchPhotoBlobUrl(systemKey, search.id).then((url) => {
        if (!url) return;
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        made.push(url);
        setUrls((prev) => ({ ...prev, [search.id]: url }));
      });
    }
    return () => {
      cancelled = true;
      made.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [systemKey, searches]);
  return urls;
}

function PastSearchPhoto({ url }: { url: string | undefined }) {
  return url ? <img className="ai-test-photo" src={url} alt="" /> : <span className="ai-test-photo is-empty" />;
}

/** One side of the comparison, drawn as the kiosk's answer sheet. */
function TrialColumn({
  title,
  side,
  chosenItemId,
  photoUrl,
  better,
}: {
  title: string;
  side: AiPhotoSearchTrialSide;
  chosenItemId: string;
  photoUrl: string | undefined;
  better?: boolean;
}) {
  const { t } = useT();
  const [best, ...others] = side.candidates;
  const looksLike = side.isFlower
    ? [
        side.flowerType ? flowerTypeLabel(side.flowerType.trim().toUpperCase(), side.flowerType) : null,
        side.colour ? colourLabel(side.colour) : null,
      ].filter((v): v is string => Boolean(v))
    : [];
  const badge = (id: string) => (id === chosenItemId ? <span className="ai-test-picked">Florist’s pick</span> : null);
  return (
    <div className={`ai-test-column${better ? " is-better" : ""}`}>
      <div className="ai-test-column-head">
        <span className="ai-test-score-label">{title}</span>
        <span className={better ? "ai-test-good" : "muted"}>Pick: {pickPlace(side.pickRank)}</span>
      </div>
      <div className="ai-test-tablet">
        <div className="ai-test-sheet">
          <div className="ai-test-strip">
            <PastSearchPhoto url={photoUrl} />
            {looksLike.length > 0 ? (
              <div className="sf-ps-looks">
                <span className="sf-ps-looks-label">{t("photoSearch.looksLike")}</span>
                {looksLike.map((word, index) => (
                  <span key={index} className="sf-ps-chip">
                    {word}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
          <h3 className="ai-test-kiosk-title">
            {!side.isFlower
              ? t("photoSearch.notAFlower")
              : side.candidates.length === 0
                ? t("photoSearch.nothingLooksLikeIt")
                : t("photoSearch.whichOne")}
          </h3>
          {best ? (
            <div className="sf-ps-group">
              <span className="sf-ps-eyebrow">{t("photoSearch.bestMatch")}</span>
              <PhotoSearchCandidateCard candidate={best} best badge={badge(best.id)} />
            </div>
          ) : null}
          {others.length > 0 ? (
            <div className="sf-ps-group">
              <span className="sf-ps-eyebrow">{t("photoSearch.alsoPossible")}</span>
              {others.map((c) => (
                <PhotoSearchCandidateCard key={c.id} candidate={c} best={false} badge={badge(c.id)} />
              ))}
            </div>
          ) : null}
        </div>
      </div>
      <span className="muted">{money(side.costEur ?? 0)}</span>
    </div>
  );
}
